import React, { useLayoutEffect, useRef } from 'react'
import {
  MessageType,
  MessageSender,
  Suggestion,
  TextChatMessage,
} from './types'
import { useChatGatewayContext, useChatMessagesContext } from '../ChatContext'
import { StyledChatMessagesLayout, StyledRegion } from './styled'
import {
  trackGAEvent,
  createGenericSelectContentEvent,
} from '@moonpig/web-core-analytics'
import { ConnectionStatus } from '../hooks/types'
import { AgentMessage } from '../components/AgentMessage'
import { ChatIntro } from '../components/ChatIntro'
import { ProductsMessage } from '../components/ProductMessage'
import { SuggestionsIntroMessage } from '../components/SuggestionsMessage'
import { ThinkingMessage } from '../components/ThinkingMessage'
import { UserMessage } from '../components/UserMessage'
import { TextLink } from '@moonpig/launchpad-components'
import { useLocaleText } from '../locale'

export const scrollChatMessagesToBottom = (
  scrollContainer: HTMLDivElement | null,
): void => {
  if (!scrollContainer) return
  const container = scrollContainer
  container.scrollTop = container.scrollHeight
}

export const ChatMessages = () => {
  const t = useLocaleText()
  const { sendMessage, connectionStatus, flagLocalInteractionToGateway } =
    useChatGatewayContext()
  const { messages, addUserMessage } = useChatMessagesContext()
  const isThinking = connectionStatus === ConnectionStatus.Sending
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    scrollChatMessagesToBottom(scrollContainerRef.current)
  }, [messages, isThinking])

  const suggestionClickHandler = (
    suggestion: Suggestion,
    suggestionOptions: number,
  ) => {
    flagLocalInteractionToGateway()
    addUserMessage(suggestion.text)
    sendMessage(suggestion.text)

    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: 'ai concierge',
        eventAction: 'suggestion click',
        eventLabel: `suggestion tile | ${suggestion.id}/${suggestionOptions}`,
      }),
    )
  }

  return (
    <StyledChatMessagesLayout>
      <StyledRegion
        ref={scrollContainerRef}
        aria-atomic={false}
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Chat messages"
        data-testid="chat-messages-container"
        role="log"
      >
        <ChatIntro />
        {messages.map((msg, idx) => {
          const key = `${msg.type}-${idx}`

          switch (msg.type) {
            case MessageType.Text:
            case MessageType.FollowUpQuestion:
              if (msg.sender === MessageSender.User) {
                return <UserMessage key={key} id={idx} msg={msg} />
              }
              return <AgentMessage key={key} id={idx} msg={msg} />
            case MessageType.Suggestions: {
              if (idx !== messages.length - 1) {
                return null
              }

              if (msg.content.supportUrl) {
                return (
                  <TextLink
                    href={msg.content.supportUrl}
                    target="_blank"
                    key={key}
                  >
                    {t('concierge_chat.help_page_link')}
                  </TextLink>
                )
              }

              if (msg.content.source === 'agent') {
                // agent suggestions are handled in InputSuggestions component
                return null
              }

              return (
                <SuggestionsIntroMessage
                  key={key}
                  id={idx}
                  suggestionClickHandler={suggestionClickHandler}
                  title={msg.content.title}
                  suggestions={msg.content.suggestions}
                />
              )
            }
            case MessageType.Products:
              return <ProductsMessage key={key} id={idx} msg={msg} />
            default:
              return (
                <AgentMessage key={key} id={idx} msg={msg as TextChatMessage} />
              )
          }
        })}
        {isThinking && <ThinkingMessage />}
      </StyledRegion>
    </StyledChatMessagesLayout>
  )
}
